import BaseInput from 'components/atoms/base-input';
import BaseTextArea from 'components/atoms/base-textarea';
import { Checkbox, Radio } from 'antd';
import i18n from 'infrastructure/i18n';
import classNames from 'classnames';

import s from './styles.module.scss';

import type {
  ICampaignQuestionAnswersSelect,
  IOutcomesFormValues,
} from 'infrastructure/interfaces';
import type { CheckboxChangeEvent } from 'antd';
import type { FormikProps } from 'formik';
import type { FC } from 'react';

const textArea = {
  en: i18n.t('smsCampaign.textAreaPlaceholderEng'),
  es: i18n.t('smsCampaign.textAreaPlaceholderEsp'),
} as const;

interface IFormItemProps {
  answer: ICampaignQuestionAnswersSelect['answers'][number];
  setFormikValues: FormikProps<IOutcomesFormValues>['setFieldValue'];
  questionGuid: string;
  answerGuids: string[];
  required?: boolean;
  label?: string;
  readonly?: boolean;
  anyAnswer?: string;
  otherAnswerGuid?: string | null;
  lang?: 'en' | 'es';
}

export const FormItem: FC<IFormItemProps> = ({
  answer,
  questionGuid,
  answerGuids,
  required,
  setFormikValues,
  label,
  readonly,
  anyAnswer,
  otherAnswerGuid,
  lang = 'en',
}) => {
  const handleInputChange = (v: string) => {
    setFormikValues(`${questionGuid}.anyAnswer`, v);

    if (!otherAnswerGuid) {
      setFormikValues(`${questionGuid}.otherAnswerGuid`, answer.guid);
    }

    if (!answerGuids?.includes(answer.guid)) {
      setFormikValues(`${questionGuid}.answers`, [...answerGuids, answer.guid]);
    }

    if (!v) {
      setFormikValues(`${questionGuid}.otherAnswerGuid`, null);
      setFormikValues(
        `${questionGuid}.answers`,
        answerGuids.filter((item) => item !== answer.guid),
      );
    }
  };

  const handleTextareaChange = (v: string) => {
    setFormikValues(`${questionGuid}.anyAnswer`, v);

    if (!otherAnswerGuid) {
      setFormikValues(`${questionGuid}.otherAnswerGuid`, answer.guid);
    }

    if (!answerGuids?.includes(answer.guid)) {
      setFormikValues(`${questionGuid}.answers`, [...answerGuids, answer.guid]);
    }

    if (!v) {
      setFormikValues(`${questionGuid}.otherAnswerGuid`, null);
      setFormikValues(
        `${questionGuid}.answers`,
        answerGuids.filter((item) => item !== answer.guid),
      );
    }
  };

  const handleCheckboxChange = (v: CheckboxChangeEvent) => {
    if (answerGuids?.includes(v.target.value)) {
      setFormikValues(
        `${questionGuid}.answers`,
        answerGuids.filter((item) => item !== v.target.value),
      );
    } else {
      setFormikValues(`${questionGuid}.answers`, [
        ...answerGuids,
        v.target.value,
      ]);
    }
  };

  if (answer.type === 'text') {
    return readonly ? (
      <div className={s['checkbox-wrapper']}>
        <Checkbox
          key={answer.guid}
          name={questionGuid}
          value={answer.guid}
          checked={Boolean(anyAnswer)}
          style={{ width: 24, height: 24 }}
          id={answer.guid}
          rootClassName={s.checkbox}
        />
        <label htmlFor={answer.guid} className={s['read-only-other']}>
          <span>Other: </span> {anyAnswer}
        </label>
      </div>
    ) : (
      <BaseInput
        key={answer.guid}
        placeholder={answer.answer}
        onChange={handleInputChange}
        name={questionGuid}
        required={required}
        disabled={readonly}
        value={anyAnswer}
        maxLength={255}
      />
    );
  }

  if (answer.type === 'textarea') {
    return readonly ? (
      <span className={classNames(s['read-only-other'], s.wrap)}>
        {anyAnswer ? `Answer: ${anyAnswer}` : 'No Answer'}
      </span>
    ) : (
      <BaseTextArea
        key={answer.guid}
        name={questionGuid}
        rows={8}
        onChange={handleTextareaChange}
        required={required}
        disabled={readonly}
        value={anyAnswer}
        placeholder={textArea[lang]}
        maxLength={1000}
      />
    );
  }

  if (answer.type === 'checkbox') {
    return (
      <div className={s['checkbox-wrapper']}>
        <Checkbox
          key={answer.guid}
          name={questionGuid}
          value={answer.guid}
          onChange={readonly ? undefined : handleCheckboxChange}
          style={{ width: 24, height: 24 }}
          id={answer.guid}
          rootClassName={s.checkbox}
          required={required}
          checked={answerGuids.includes(answer.guid)}
        />
        <label htmlFor={answer.guid} style={{ fontSize: readonly ? 13 : 14 }}>
          {label}
        </label>
      </div>
    );
  }

  if (answer.type === 'radio') {
    return (
      <div className={s['radio-wrapper']}>
        <Radio
          key={answer.guid}
          name={questionGuid}
          value={answer.guid}
          checked={answerGuids.includes(answer.guid)}
          onChange={
            readonly
              ? undefined
              : (v) =>
                  setFormikValues(`${questionGuid}.answers`, [v.target.value])
          }
          rootClassName={s.radio}
          id={answer.guid}
          required={required}
        />
        <label htmlFor={answer.guid} style={{ fontSize: readonly ? 13 : 14 }}>
          {label}
        </label>
      </div>
    );
  }

  return null;
};
